import {
    type Block,
    type Change,
    type Document,
    Fragment,
    type SelectionRange,
    TextRange,
} from '@gitbook/slate';
import type { DeepReadonly } from '@gitbook/util-immutable';

import type { BlockTableData } from '../types';

/**
 * Selection in the table stored under the data.table property of the Slate selection.
 */
export interface TableSelection {
    recordId?: string;
    definitionId?: string;

    /**
     * ID of the realtime client (from Hive)
     * undefined if the selection is from the current user.
     */
    clientId?: string;
}

/**
 * Move table selection.
 */
export function moveTableSelection(
    change: Change,
    node: Block | string,
    newSelection: TableSelection,
    focus: 'end' | 'start' | 'select' = 'end'
) {
    const { recordId, definitionId } = newSelection;

    // Assert the latest version of the node
    const tableBlock = change.value.document.assertBlock(
        typeof node === 'string' ? node : node.key
    );

    if (recordId && definitionId) {
        const tableData = tableBlock.data.toFrozenObject<BlockTableData>();
        const recordValue = tableData.records[recordId]?.values[definitionId];
        const colDef = tableData.definition[definitionId];

        if (colDef?.type === 'text' && typeof recordValue === 'string') {
            const fragment = tableBlock.getFragmentByKey(recordValue);

            // If we can't find a fragment, we focus the table
            if (!fragment) {
                change.focus();
                return;
            }

            // If selection is not in the fragment we update it, otherwise we keep it untouched
            if (!change.value.selection.hasFocusIn(fragment)) {
                if (focus === 'end') {
                    change.collapseToEndOf(fragment);
                } else if (focus === 'start') {
                    change.collapseToStartOf(fragment);
                } else {
                    change.collapseToStartOf(fragment).moveFocusToEndOf(fragment);
                }
                change.focus();
            }
        } else {
            change.collapseToStartOf(tableBlock).select({
                data: {
                    table: newSelection,
                },
            });
        }
    } else {
        change.collapseToStartOf(tableBlock).select({
            data: {
                table: newSelection,
            },
        });
    }
}

/**
 * Compute the selection for the table.
 */
export function getTableSelection(
    document: Document,
    selection: SelectionRange,
    node: Block
): TableSelection {
    const clientId = selection.data.get('clientId');

    if (!selection.hasFocusIn(node) || !TextRange.isTextRange(selection) || !selection.startKey) {
        return { clientId };
    }

    const ancestors = document.getAncestors(selection.startKey);
    if (ancestors) {
        // Find the fragment that belongs to this table
        const tableFragment = ancestors.find((ancestor) => {
            return Fragment.isFragment(ancestor) && node.fragments.has(ancestor.fragmentKey);
        });

        if (tableFragment && Fragment.isFragment(tableFragment)) {
            const tableData = node.data.toFrozenObject<BlockTableData>();
            return {
                clientId,
                ...(lookupRecordByFragmentKey(tableData, tableFragment.fragmentKey) || {}),
            };
        }
    }

    const tableSelectionData = selection.data.get('table');
    return { ...tableSelectionData, clientId };
}

/**
 * Lookup a definition for a text field in the table.
 */
function lookupRecordByFragmentKey(
    data: DeepReadonly<BlockTableData>,
    fragmentKey: string
): TableSelection | null {
    for (const record of Object.entries(data.records)) {
        for (const definitionId in record[1].values) {
            if (record[1].values[definitionId] === fragmentKey) {
                return {
                    recordId: record[0],
                    definitionId,
                };
            }
        }
    }

    return null;
}
